/**
 * Utility functions for extracting, formatting, and filtering domain hashtags (#tags)
 * and calculating query relevance for ranking premium domains.
 */

/**
 * Extracts hashtag tags from a text string (e.g. "Crypto wallet #crypto #web3" -> ["crypto", "web3"])
 * @param {string} text
 * @returns {string[]}
 */
export function extractHashtags(text) {
  if (!text || typeof text !== 'string') return [];
  const matches = text.match(/#([a-zA-Z0-9_-]+)/g);
  if (!matches) return [];
  const unique = new Set(
    matches.map(m => m.replace(/^#/, '').trim().toLowerCase()).filter(Boolean)
  );
  return Array.from(unique);
}

/**
 * Returns clean description text with trailing or inline hashtags stripped for cleaner display
 * @param {string} text
 * @returns {string}
 */
export function stripHashtags(text) {
  if (!text || typeof text !== 'string') return '';
  return text.replace(/#([a-zA-Z0-9_-]+)/g, '').replace(/\s{2,}/g, ' ').trim();
}

/**
 * Gathers all unique hashtags across an array of domain items
 * @param {Array} domains
 * @returns {string[]}
 */
export function getUniqueHashtags(domains) {
  if (!Array.isArray(domains)) return [];
  const counts = new Map();
  for (const d of domains) {
    const tags = extractHashtags(d.description);
    for (const tag of tags) {
      counts.set(tag, (counts.get(tag) || 0) + 1);
    }
  }
  // Sort by popularity / frequency
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(entry => entry[0]);
}

/**
 * Computes a relevance score (0..100+) of a domain relative to a user search query
 * @param {Object} domain
 * @param {string} query
 * @returns {number}
 */
export function scoreDomainRelevance(domain, query) {
  if (!domain || !query || typeof query !== 'string') return 0;
  const rawQ = query.trim().toLowerCase();
  if (!rawQ) return 0;

  // If query starts with #, strict tag match gets highest priority
  const isTagSearch = rawQ.startsWith('#');
  const q = rawQ.replace(/^#/, '').trim();
  if (!q) return 0;

  const domainName = (domain.name || '').toLowerCase().trim();
  const domainBase = domainName.split('.')[0] || domainName;
  const desc = (domain.description || '').toLowerCase();
  const tags = extractHashtags(domain.description);

  let score = 0;

  // 1. Exact Name Matches (Highest Priority)
  if (domainName === q || domainName === `${q}.tg`) {
    score += 120;
  } else if (domainBase === q) {
    score += 100;
  }

  // 2. Exact or Partial Hashtag Matches
  if (tags.includes(q)) {
    score += isTagSearch ? 95 : 70;
  } else if (tags.some(t => t.includes(q) || q.includes(t))) {
    score += isTagSearch ? 60 : 45;
  }

  // 3. Domain Prefix Matches
  if (domainName.startsWith(q) || domainBase.startsWith(q)) {
    score += 65;
  }

  // 4. Substring in Domain Name
  if (domainName.includes(q)) {
    score += 50;
  }

  // 5. Match in Domain Description
  if (desc.includes(q)) {
    score += 30;
  }

  // 6. Character / substring similarity heuristic
  if (score === 0 && domainBase.length >= 3 && q.length >= 3) {
    let matchedLetters = 0;
    for (const ch of q) {
      if (domainBase.includes(ch)) matchedLetters++;
    }
    const overlapRatio = matchedLetters / Math.max(q.length, domainBase.length);
    if (overlapRatio >= 0.6) {
      score += Math.round(overlapRatio * 25);
    }
  }

  return score;
}

/**
 * Returns a simplified registry source label (WHOIS, RDAP, NIC) without technical suffixes.
 * @param {string} method
 * @returns {string}
 */
export function simplifySource(method) {
  if (!method) return 'WHOIS';
  const m = String(method).toUpperCase();
  if (m.includes('RDAP')) return 'RDAP';
  if (m.includes('NIC') || m.includes('HTTP')) return 'NIC';
  return 'WHOIS';
}

/**
 * Normalizes domain input and converts IDN / Cyrillic to punycode.
 * @param {string} input
 * @returns {{ unicode: string, punycode: string, isIdn: boolean, isValid: boolean, error: string }}
 */
export function formatDomainPunycode(input) {
  if (!input || typeof input !== 'string') return { unicode: '', punycode: '', isIdn: false, isValid: false, error: '' };
  let raw = input.trim().toLowerCase().replace(/^https?:\/\//i, '').replace(/^www\./i, '');
  raw = raw.replace(/\s+/g, '-').slice(0, 30);
  if (!raw) return { unicode: '', punycode: '', isIdn: false, isValid: false, error: '' };

  const fullUnicode = raw.includes('.') ? raw : `${raw}.tg`;
  if (fullUnicode.length > 30) {
    return { unicode: fullUnicode, punycode: '', isIdn: false, isValid: false, error: 'Domain name must not exceed 30 characters' };
  }

  let punycode = fullUnicode;
  let isIdn = false;

  try {
    const urlObj = new URL(`http://${encodeURI(fullUnicode)}`);
    punycode = urlObj.hostname || fullUnicode;
    if (punycode !== fullUnicode && (punycode.includes('xn--') || punycode.startsWith('xn--'))) {
      isIdn = true;
    }
  } catch (_) {
    return { unicode: fullUnicode, punycode: '', isIdn: false, isValid: false, error: 'Invalid international domain format' };
  }

  const rfcRegex = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9-]{2,63}$/i;
  const isValid = rfcRegex.test(punycode);

  return {
    unicode: fullUnicode,
    punycode,
    isIdn,
    isValid,
    error: isValid ? '' : 'Invalid domain format. Example: startup.tg or brand.com'
  };
}

/**
 * Formats a numeric price into a compact string representation (e.g., 20000 -> "20K", 100000 -> "100K", 1500000 -> "1.5M")
 * when price doesn't fit in button widths.
 * @param {number|string} val
 * @returns {string}
 */
export function formatCompactPrice(val) {
  if (val === null || val === undefined || val === '') return '0';
  const num = Number(val);
  if (isNaN(num)) return String(val);

  if (num >= 1000000) {
    const m = num / 1000000;
    return (m % 1 === 0 ? m : m.toFixed(1).replace(/\.0$/, '')) + 'M';
  }
  if (num >= 10000) {
    const k = num / 1000;
    return (k % 1 === 0 ? k : k.toFixed(1).replace(/\.0$/, '')) + 'K';
  }
  return num.toLocaleString(undefined, { maximumFractionDigits: 0 });
}
